import { WistiaPlayer } from '@wistia/wistia-player-react';
import { isToday } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation } from 'urql';

import { useContactSalesMutation, useGetLoggedInUser } from '@revelio/auth';
import {
  CTAInteractionTrackingEvents,
  TutorialTrackingEvents,
  useTourStore,
} from '@revelio/core';
import {
  type KeyValueResp,
  MetadataKey,
  type User,
} from '@revelio/data-access';
import { useDisclosure } from '@revelio/ui';

import { useToast } from '../../shared/hooks/useToast';
import {
  DEFAULT_TUTORIAL_VIDEO_ID,
  findTutorialVideoById,
  tutorialVideoCoversPath,
  type VideoData,
  videoMetadata,
} from '../../shared/ui/tutorial/Tutorial';
import { UpdateUserMutation } from '../adminRewrite/userOperations';
import { hasCompletedTour, useIsTourDone } from '../tour/use-is-tour-done';
import {
  toastContactSalesAlreadyNotified,
  toastContactSalesRequestError,
  toastContactSalesRequestSuccess,
} from '../trialUser/contact-sales-toast';
import { useFreeTrialAlertModalState } from '../trialUser/useFreeTrialAlertModalState';
import modalStyles from './tutorial-modal.module.css';
import { TutorialModalContent } from './tutorial-modal-content';
import { isTutorialSectionAllowed } from './tutorial-section-access';
import { useThemedWistiaPlayerColor } from './use-themed-wistia-player-color';

const Tutorial = () => {
  const { loggedInUser, refetch: refetchUserData } = useGetLoggedInUser();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [videoId, setVideoIdState] = useState<string | null>(
    DEFAULT_TUTORIAL_VIDEO_ID
  );

  const [tourComplete, setTourComplete] = useState<boolean>(false);

  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const themedPlayerColor = useThemedWistiaPlayerColor();
  const { email, metadata } = loggedInUser;
  const serverWatchedIds = useMemo(() => {
    return new Set<string>(
      JSON.parse(
        metadata?.find(
          (item: KeyValueResp | null) =>
            item?.key === MetadataKey.StepsCompleted
        )?.value || '[]'
      )
    );
  }, [metadata]);

  /**
   * Ids recorded during this session. `metadata` is refetched after every save
   * and comes back without the id until the write lands, so a set derived from
   * the server alone drops in and out — the rail's checkmark flickered on every
   * progress event and each one fired another save. Holding them locally makes
   * the union monotonic. The ref mirrors the state so progress events firing
   * within one render still see the id.
   */
  const [sessionWatchedIds, setSessionWatchedIds] = useState<
    ReadonlySet<string>
  >(() => new Set<string>());
  const recordedIdsRef = useRef<Set<string>>(new Set<string>());

  const userWatchedIds = useMemo(() => {
    return new Set<string>([...serverWatchedIds, ...sessionWatchedIds]);
  }, [serverWatchedIds, sessionWatchedIds]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (userWatchedIds.has('[')) {
      handleUpdateMetadata(loggedInUser, [
        {
          key: MetadataKey.StepsCompleted,
          value: JSON.stringify([]),
        },
      ]);
    }
  }, []);

  const { days } = useFreeTrialAlertModalState();

  const [, updateUser] = useMutation(UpdateUserMutation);

  const mixpanelContactSales = () => {
    mixpanel.track(CTAInteractionTrackingEvents.CONTACT_SALES_CLICK, {
      days_left: days,
    });
  };

  const { mutate: contactSales } = useContactSalesMutation();

  const handleContactSales = () => {
    mixpanelContactSales();

    const contactedSales = localStorage.getItem('contacted-sales') || '';
    if (!contactedSales || !isToday(new Date(contactedSales))) {
      contactSales(
        { email, trialUpgrade: location.pathname },
        {
          onSuccess: () => {
            localStorage.setItem('contacted-sales', new Date().toDateString());
            toastContactSalesRequestSuccess(toast);
          },
          onError: () => {
            toastContactSalesRequestError(toast);
          },
        }
      );
    } else {
      toastContactSalesAlreadyNotified(toast);
    }
  };

  const isVideoOpen = useTourStore((s) => s.isVideoOpen);
  const videoOpenSource = useTourStore((s) => s.videoOpenSource);
  const storeVideoId = useTourStore((s) => s.videoId);
  const isTourDone = useIsTourDone();
  const setIsVideoOpen = useTourStore((s) => s.setIsVideoOpen);
  const setIsTourComplete = useTourStore((s) => s.setIsTourComplete);
  const setStoreVideoId = useTourStore((s) => s.setVideoId);

  useEffect(() => {
    if (isVideoOpen) {
      onOpen();
    }
  }, [isVideoOpen, onOpen]);

  // One `tutorial opened` per open of the modal, whichever entry point asked.
  // The ref keeps a video switch inside an open modal from counting again.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      mixpanel.track(TutorialTrackingEvents.TUTORIAL_OPENED, {
        video_id: videoId,
        video_name: findTutorialVideoById(videoId)?.name,
        ...(videoOpenSource !== null && { source: videoOpenSource }),
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, videoId, videoOpenSource]);

  useEffect(() => {
    if (storeVideoId) {
      setVideoIdState(storeVideoId);
    }
  }, [storeVideoId]);

  useEffect(() => {
    if (isTourDone) {
      setTourComplete(true);
    }
  }, [isTourDone]);

  const handleVideoProgress = (
    percentWatched: number,
    currentVideoId: string
  ) => {
    if (percentWatched < 0.1) return;
    if (recordedIdsRef.current.has(currentVideoId)) return;
    if (serverWatchedIds.has(currentVideoId)) return;

    recordedIdsRef.current.add(currentVideoId);
    setSessionWatchedIds((current) => new Set(current).add(currentVideoId));

    handleUpdateMetadata(loggedInUser, [
      {
        key: MetadataKey.StepsCompleted,
        value: JSON.stringify([...userWatchedIds, currentVideoId]),
      },
    ]);

    const currentVideoData = findTutorialVideoById(currentVideoId);

    mixpanel.track(TutorialTrackingEvents.WATCH_TUTORIAL_VIDEO, {
      video_id: currentVideoId,
      video_name: currentVideoData?.name,
      video_duration: currentVideoData?.duration,
    });
  };

  const handleUpdateMetadata = (
    loggedInUser: User = {},
    newMeta: KeyValueResp[]
  ) => {
    const metaFieldsToExclude = [MetadataKey.ExpirationDate];

    return new Promise((resolve, reject) => {
      if (isEmpty(loggedInUser)) {
        reject('No logged in user provided');
      }

      const newMetaKeys = newMeta.map((item) => item.key);

      const currentMetaObjects = (loggedInUser.metadata || []).filter(
        (item: KeyValueResp | null) =>
          !newMetaKeys.includes(item?.key) &&
          !metaFieldsToExclude.includes(item?.key as MetadataKey)
      );

      const updatedMeta = [...currentMetaObjects, ...newMeta];

      updateUser({
        id: loggedInUser?.id as string,
        metadata: updatedMeta,
      })
        .then(() => {
          // update user object on update success
          refetchUserData();
          resolve('User updated');
        })
        .catch(() => {
          reject('Something went wrong');
        });
    });
  };

  const userTourComplete = useMemo(
    () => hasCompletedTour(metadata),
    [metadata]
  );

  const activeVideo = findTutorialVideoById(videoId);
  const player = activeVideo?.mediaId ? (
    <WistiaPlayer
      mediaId={activeVideo.mediaId}
      playerColor={themedPlayerColor}
      autoplay={false}
      onPercentWatchedChange={({ detail: { percentWatched } }) => {
        if (videoId) {
          handleVideoProgress(percentWatched, videoId);
        }
      }}
      onEnded={() => {
        mixpanel.track(TutorialTrackingEvents.END_TUTORIAL_VIDEO, {
          video_id: videoId,
          video_name: activeVideo.name,
        });
      }}
    />
  ) : (
    <div className={modalStyles.videoPlaceholder}>
      {activeVideo && (
        <img src={activeVideo.thumbnail} alt="" aria-hidden="true" />
      )}
      <span>Video coming soon</span>
    </div>
  );

  const handleOpenSection = (video: VideoData) => {
    const [path] = video.paths;
    onClose();
    if (path) {
      navigate(path);
    }
  };

  const userTabs = useMemo(() => loggedInUser.tabs ?? [], [loggedInUser.tabs]);
  const canOpenSection = isTutorialSectionAllowed(
    activeVideo?.paths[0],
    userTabs
  );

  /**
   * Nothing to offer when the viewer is already on the page this video covers.
   * Otherwise offer the page — unless they are not entitled to it, in which
   * case opening it drops them on a route the router will not serve, so upsell
   * instead.
   */
  const resolvePrimaryAction = () => {
    if (!activeVideo || activeVideo.paths.length === 0) return null;
    // Same predicate the catalog picks the video with — an exact match would
    // miss nested step pages like '/data-builder/columns'.
    if (tutorialVideoCoversPath(activeVideo, location.pathname)) return null;
    if (!canOpenSection) {
      return { label: 'Contact Sales', onClick: handleContactSales };
    }
    return {
      label: `Open ${activeVideo.name.toLowerCase()}`,
      onClick: () => handleOpenSection(activeVideo),
    };
  };

  const primaryAction = resolvePrimaryAction();

  return (
    <TutorialModalContent
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={() => {
        setIsVideoOpen(false);
        setIsTourComplete(true);
      }}
      closeOnBackdropClick={tourComplete || userTourComplete}
      groups={videoMetadata}
      activeVideo={activeVideo}
      watchedIds={userWatchedIds}
      onSelectVideo={setStoreVideoId}
      primaryAction={primaryAction}
      player={player}
      descriptionSuffixFor={(video) =>
        isTutorialSectionAllowed(video.paths[0], userTabs) ? null : (
          <span className={modalStyles.contactSalesNote}>
            {` `}Your plan does not include this section — contact sales to get
            started.
          </span>
        )
      }
    />
  );
};

export default Tutorial;
